import { cn } from '@app/utils/cn';
import { useLocalStorage } from '@app/utils/hooks/useLocalStorage';
import { IconComponent } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { To } from 'react-router-dom';

import { Button } from '../Button';
import { Collapse, Expand } from '../Icons';

import { MobileSidebarNavigationItem } from './MobileSidebarNavigationItem';
import { SidebarNavigationItem } from './SidebarNavigationItem';

export interface NavigationItem {
  title: string;
  subtitle: string;
  icon: IconComponent;
  link: To;
}

interface Props {
  items: NavigationItem[];
  activePath: string;
  storageKey: string;
}

export const SidebarNavigation = ({ items, activePath, storageKey }: Props) => {
  const [isExpanded, setIsExpanded] = useLocalStorage({ asJson: true, defaultValue: true, key: `${storageKey}_expanded` });
  const expanderId = React.useId();
  const { t } = useTranslation(['common']);

  return (
    <>
      <div
        className={cn(
          'box sticky top-[calc(var(--headerHeight)+var(--formHeadingHeight))] hidden overflow-hidden transition-width lg:flex lg:flex-col',
          {
            'w-20 gap-2 pt-3': !isExpanded,
            'w-80': isExpanded,
          },
        )}
      >
        <div className="h-12 space-y-2">
          <div className={cn('flex items-center justify-between', { 'flex-col gap-1': !isExpanded })}>
            {isExpanded ?
              <h3 className="heading-3">{t('common:sidebar_navigation_header')}</h3>
            : <h3 className="text-neutral-80 body-6">{t('common:sidebar_navigation_header_collapsed')}</h3>}
            <Button
              variant="text"
              color="secondary"
              icon={isExpanded ? Collapse : Expand}
              onClick={() => setIsExpanded(!isExpanded)}
              aria-expanded={isExpanded}
              aria-controls={expanderId}
              aria-label={t('common:sidebar_toggle')}
            />
          </div>
          <hr className={cn('w-full text-neutral-90', { hidden: isExpanded })} />
        </div>
        <div className="space-y-1" id={expanderId}>
          {items.map(item => (
            <SidebarNavigationItem
              key={item.title}
              icon={item.icon}
              title={item.title}
              subtitle={item.subtitle}
              link={item.link}
              isExpanded={isExpanded}
              isActive={item.link === activePath}
            />
          ))}
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 z-topBar bg-white py-2 lg:hidden">
        <div className="flex justify-center gap-4 sm:gap-10 xs:gap-2">
          {items.map(item => (
            <MobileSidebarNavigationItem
              key={item.title}
              icon={item.icon}
              title={item.title}
              link={item.link}
              isActive={item.link === activePath}
            />
          ))}
        </div>
      </div>
    </>
  );
};
